import React, { useRef, useState } from "react";
import { useRouter } from "next/router";
import Image from "next/image";
import Link from "next/link";
import Toggle from "./Toggle";
import vinyl from "../images/vinyl.webp";
import radiopng from "../images/radio.webp";
import AudioPlayer from "react-h5-audio-player";
import "react-h5-audio-player/lib/styles.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faExclamationTriangle } from "@fortawesome/free-solid-svg-icons";
import { useSrcErrorContext } from "./SourceContextWrapper";
import { useCurrentPlayingContext } from "./CurrentStationContextWrapper";
import { faPauseCircle } from "@fortawesome/free-regular-svg-icons";

import StreamingMetadataComponent from "./useFetchAudioFiles";
import LazyLoadImage from "./lazyLoadImage";

const Mediaplayer = () => {
  const imageRef = useRef(null);
  const router = useRouter();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalUrl, setModalUrl] = useState("");

  const { srcError, setSrcError } = useSrcErrorContext();
  const { currentStation, audioPlayerRef } = useCurrentPlayingContext();

  const { setIsopen, setQueryState } = Toggle();

  //======= Navigation functions =========
  const handleClick = () => {
    setIsopen(false);
    setQueryState(false);
  };

  //======= Display Spinner and Equalizer functions =========
  const animateStream = () => {
    // if (error === false) {
    //   return { display: "none" };
    // }
    if (srcError !== "play") {
      return { display: "none" };
    }
  };

  //======= Redirect page if source is url ends .html =========
  // const handleHTMLSrc = (e) => {
  //   if (currentStation?.stream && currentStation?.stream.endsWith(".html")) {
  //     const userConfirmed = window.confirm(
  //       "This audio source is an HTML page. Do you want to open it in a new tab?"
  //     );
  //     if (userConfirmed) {
  //       window.open(currentStation?.stream, "_blank"); // Open the URL in a new tab
  //     }
  //   }
  // };

  const handleHTMLSrc = () => {
    if (currentStation?.stream && currentStation?.stream.endsWith(".html")) {
      setModalUrl(currentStation?.stream); // Save the URL to open if the user confirms
      setIsModalOpen(true); // Open the modal
    }
  };

  const confirmRedirect = () => {
    window.open(modalUrl, "_blank"); // Open the URL in a new tab
    setIsModalOpen(false); // Close the modal
  };

  const cancelRedirect = () => {
    setIsModalOpen(false); // Close the modal without redirecting
  };

  const showDiv = () => {
    // Hide player when not playing on homepage OR when not on homepage
    const isHidden = srcError !== "play" && router.pathname === "/";

    if (isHidden) {
      document.body.classList.remove("has-player");
      return { display: "none" };
    }

    document.body.classList.add("has-player");
    return {};
  };

  function removeFM(params) {
    // Ensure params is a string to avoid TypeError
    if (typeof params === "string" && params.includes("FM")) {
      return params.replace("FM", "").trim();
    }
    // Return the original params or an empty string if it's undefined or not a string
    return params || "";
  }

  // const { data: currentUser } = useSWR("currentStation", key => {
  //     const value = localStorage.getItem(key);
  //     return !!value ? JSON.parse(value) : undefined;
  // });

  return (
    <div className="player-outer" style={showDiv()}>
      <div className="player-inner">
        <div className="player-grid">
          {/* Station Info */}
          <div className="player-station">
            <Link
              href={
                currentStation?.worldradiostation === "yes"
                  ? `/radio/${currentStation.id}`
                  : `/stream/${currentStation.id}/${currentStation.name}`
              }
              style={{ width: "100%" }}
            >
              <div className="station-info" onClick={handleClick}>
                <div
                  className="station-icon"
                  style={{
                    height: "40px",
                    aspectRatio: "1/1",
                    width: "40px",
                  }}
                >
                  <LazyLoadImage
                    ref={imageRef}
                    src={currentStation?.icon}
                    alt="logo"
                    placeholder={radiopng.src}
                    fallback={radiopng.src}
                    width="100%"
                    height="100%"
                  />
                </div>

                <div className="station-meta">
                  <div className="station-title">
                    <div className="station-title__name">
                      {currentStation?.name}
                    </div>
                    {currentStation?.Frequency && (
                      <span className="station-freq">
                        {removeFM(currentStation?.Frequency)}{" "}
                        <span>
                          {currentStation?.worldradiostation === "yes"
                            ? "kb/s"
                            : "MHz"}
                        </span>
                      </span>
                    )}
                  </div>
                  {srcError && (
                    <StreamingMetadataComponent
                      params={currentStation?.stream}
                    />
                  )}
                </div>
              </div>
            </Link>
          </div>

          <div className="player-controls">
            {/* Player Status */}
            <div className="player-status">
              {srcError === "play" && (
                <div className="status-playing">
                  <div className="vinyl-container">
                    <Image src={vinyl} alt="Now Playing" fill priority />
                  </div>
                  <span>Live On Air</span>
                </div>
              )}
              {srcError === "offline" && (
                <div className="status-offline">
                  <FontAwesomeIcon icon={faExclamationTriangle} />
                  <span>Offline</span>
                </div>
              )}
              {srcError === "pause" && (
                <div className="status-paused">
                  <FontAwesomeIcon icon={faPauseCircle} />
                  <span>Paused</span>
                </div>
              )}
            </div>

            {/* Audio Controls */}
            <div className="player-audio">
              <AudioPlayer
                ref={audioPlayerRef}
                autoPlay
                src={currentStation?.stream}
                onPlay={(e) => {
                  setSrcError(e.type);
                  handleHTMLSrc();
                }}
                onPause={(e) => setSrcError(e.type)}
                onAbort={(e) => setSrcError(e.type)}
                onPlayError={(e) => setSrcError("offline")}
                customControlsSection={["MAIN_CONTROLS", "VOLUME_CONTROLS"]}
                customProgressBarSection={[]}
                showJumpControls={false}
                showSkipControls={false}
                showLoopControl={false}
                layout="horizontal-reverse"
              />
            </div>
          </div>
        </div>
      </div>
      {isModalOpen && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-content__text">
              <small>
                <strong>Confirm Redirect</strong>
              </small>
              <small>The audio source is a webpage. Open in new tab?</small>
            </div>
            <div className="modal-buttons">
              <button onClick={confirmRedirect} className="confirm-btn">
                Yes
              </button>
              <button onClick={cancelRedirect} className="cancel-btn">
                No
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Mediaplayer;
